import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import {
  ArrowLeft,
  PaperPlaneTilt,
  CalendarBlank,
  MapPin,
  Users,
  ForkKnife,
  Phone,
  EnvelopeSimple,
  CircleNotch,
  CheckCircle,
  Lightning,
  ChatCircle,
  XCircle,
  Lock,
} from "@phosphor-icons/react";

/**
 * OwnerInquiryDetail — Phase 2 reply path.
 *
 * Owner lands here from the email CTA "Reply via TruckTrackr →" which
 * deep-links to /dashboard/inquiries/:recipientId. They see the
 * inquiry, type a reply, hit send. We relay via Resend with
 * Reply-To = owner's email, so the host's inbox shows the owner's
 * personal email for any further back-and-forth.
 *
 * Pro-gated: free trucks see a "Reply via your email" CTA + upgrade
 * nudge. The server also enforces the Pro gate as defence-in-depth.
 */

const QUICK_REPLIES = [
  {
    key: "available",
    label: "I'm available",
    text: (truckName, eventDate) =>
      `Hi there!\n\nThanks for thinking of ${truckName} for your event. We're available on ${eventDate} and would love to be part of it.\n\nA few quick questions:\n- Approximate menu preference?\n- Service start time and duration?\n- Power / water access on site?\n\nReply to this email and I'll send a custom quote within a day.`,
  },
  {
    key: "quote",
    label: "Send a quote",
    text: (truckName) =>
      `Hi there!\n\nThanks for the inquiry. Here's a preliminary quote from ${truckName}:\n\n- Per-head price: $\n- Minimum guarantee: $\n- Travel/setup fee: $\n- Service window: \n\nPricing is locked in once we have a signed agreement + 25% deposit. Happy to jump on a call if easier — what's a good time?`,
  },
  {
    key: "more_info",
    label: "Need more info",
    text: (truckName) =>
      `Hi there!\n\nThanks for reaching out to ${truckName}! Before I can confirm, I need a bit more info:\n\n- Address or venue name\n- Service start + end time\n- Estimated guest count (final)\n- Any menu must-haves or restrictions\n- Power available on site? (most trucks need a 30/50 amp hookup)\n\nReply with those details and I'll get a quote out today.`,
  },
];

export default function OwnerInquiryDetail() {
  const { recipientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [reply, setReply] = useState("");
  const [templateUsed, setTemplateUsed] = useState(null);
  const [sending, setSending] = useState(false);
  const [declineMode, setDeclineMode] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/owner/event-inquiries/${recipientId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
          return;
        }
        if (e?.response?.status === 404) {
          setError("This inquiry doesn't exist, has expired, or belongs to another owner.");
        } else {
          setError(formatApiError(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [recipientId, navigate]);

  function applyTemplate(t) {
    const truckName = data?.truck?.truck_name || "our truck";
    const eventDate = data?.inquiry?.event_date_start || "your date";
    setReply(t.text(truckName, eventDate));
    setTemplateUsed(t.key);
  }

  async function handleSend(kind) {
    if (reply.trim().length < 10) {
      toast.error("Add a bit more to your message (at least 10 characters).");
      return;
    }
    setSending(true);
    try {
      await api.post(`/owner/event-inquiries/${recipientId}/reply`, {
        message: reply.trim(),
        kind,
        template_used: templateUsed,
      });
      toast.success(kind === "decline" ? "Decline sent." : "Reply sent. Host has been emailed.");
      // Refresh the recipient row so the status pill updates
      const r = await api.get(`/owner/event-inquiries/${recipientId}`);
      setData(r.data);
      setReply("");
      setDeclineMode(false);
    } catch (e) {
      // formatApiError expects the FastAPI `detail` field, not the
      // axios error itself — otherwise the toast prints the unhelpful
      // "AxiosError: Request failed with status code 422" instead of
      // the real reason.
      const detail = e?.response?.data?.detail ?? e?.message ?? e;
      toast.error(formatApiError(detail));
    } finally {
      setSending(false);
    }
  }

  async function handleDismiss() {
    // Optimistic UX: navigate back to dashboard immediately and surface
    // a 5-second "Undo" toast — feels snappier than waiting on the round
    // trip + soft-deletes the row server-side so it doesn't return on
    // the next inbox load.
    setDismissing(true);
    try {
      await api.post(`/owner/event-inquiries/${recipientId}/dismiss`);
      toast.success("Dismissed", {
        description: "Hidden from your inbox.",
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/owner/event-inquiries/${recipientId}/dismiss`);
              toast.success("Restored.");
              navigate(`/dashboard/inquiries/${recipientId}`);
            } catch (e) {
              toast.error(formatApiError(e) || "Couldn't undo");
            }
          },
        },
      });
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail ?? e) || "Couldn't dismiss");
    } finally {
      setDismissing(false);
    }
  }

  // -------------------- States --------------------
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <CircleNotch weight="bold" className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="text-center max-w-md">
          <XCircle weight="duotone" className="h-12 w-12 mx-auto text-muted-foreground" />
          <h2 className="mt-4 font-display text-xl font-semibold">Inquiry not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error || "This inquiry doesn't exist or you don't have access."}</p>
          <Link to="/dashboard" className="mt-5 inline-block">
            <Button variant="outline">Back to dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { recipient, inquiry, truck, owner_is_pro } = data;
  const alreadyReplied = recipient.status === "replied";
  const isLockedOut = recipient.status === "expired_silent";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10" data-testid="owner-inquiry-detail">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition mb-5">
          <ArrowLeft weight="bold" className="h-4 w-4" />
          Back to dashboard
        </Link>

        {/* Header */}
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-1">
            Event Inquiry · {truck?.truck_name || "Your truck"}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {inquiry.customer_name} wants to book you
          </h1>
        </div>

        {/* Status pill */}
        <div className="mb-5 flex items-center gap-2 flex-wrap">
          {alreadyReplied ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-1 rounded-full">
              <CheckCircle weight="fill" className="h-3.5 w-3.5" />
              {recipient.response_kind === "decline" ? "Declined" : "Replied"}
              {recipient.responded_at && (
                <span className="opacity-70 ml-1">
                  · {new Date(recipient.responded_at).toLocaleDateString()}
                </span>
              )}
            </span>
          ) : isLockedOut ? (
            <span
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-muted text-muted-foreground px-2.5 py-1 rounded-full"
              data-testid="owner-inquiry-locked-pill"
            >
              <XCircle weight="fill" className="h-3.5 w-3.5" />
              Window closed
            </span>
          ) : recipient.status === "viewed" ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-1 rounded-full">
              <Lightning weight="fill" className="h-3.5 w-3.5" />
              Open · awaiting your reply
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              <Lightning weight="fill" className="h-3.5 w-3.5" />
              New
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            Received {new Date(recipient.delivered_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
          {/* Dismiss action — only meaningful after the owner has
              replied or declined (no point dismissing a brand-new lead
              before deciding). Soft-archives + offers a 5s undo toast. */}
          {alreadyReplied && (
            <button
              type="button"
              onClick={handleDismiss}
              disabled={dismissing}
              className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition disabled:opacity-50"
              data-testid="owner-inquiry-dismiss-btn"
            >
              {dismissing ? (
                <CircleNotch weight="bold" className="h-3 w-3 animate-spin" />
              ) : (
                <XCircle weight="bold" className="h-3.5 w-3.5" />
              )}
              Dismiss
            </button>
          )}
        </div>

        {/* Event details grid */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <DetailTile icon={CalendarBlank} label="Event date" value={inquiry.event_date_start} />
          <DetailTile icon={MapPin} label="Location" value={inquiry.event_zip} />
          <DetailTile icon={Users} label="Party size" value={`${inquiry.party_size} guests`} />
          <DetailTile
            icon={ForkKnife}
            label="Cuisine"
            value={
              (inquiry.cuisine_preferences || []).length > 0
                ? inquiry.cuisine_preferences.join(", ")
                : "Any (host is flexible)"
            }
          />
        </div>

        {/* Why-you-were-matched echo — explains the algorithm to the owner
            so it feels less like spam ("we got you because your tags
            matched 'tacos, mexican'"). Empty list = host left cuisine
            blank, so we just say "location match". */}
        <div
          className="mb-5 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-sm flex flex-wrap items-center gap-2"
          data-testid="owner-inquiry-match-reason"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
            Why we matched you
          </span>
          {(recipient.matched_cuisines || []).length > 0 ? (
            <>
              {(recipient.matched_cuisines || []).slice(0, 6).map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 text-[12px] font-medium bg-white text-primary px-2 py-0.5 rounded-full ring-1 ring-primary/20"
                >
                  <ForkKnife weight="fill" className="h-2.5 w-2.5" />
                  {c}
                </span>
              ))}
            </>
          ) : (
            <span className="text-[12px] text-muted-foreground">
              You&apos;re in range and the host left cuisine flexible.
            </span>
          )}
        </div>

        {/* Message from host */}
        <div className="rounded-2xl border border-border bg-card p-5 mb-5">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
            Message from {inquiry.customer_name}
          </div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{inquiry.message}</p>
        </div>

        {/* Contact info */}
        <div className="rounded-2xl border border-border bg-muted/30 p-4 mb-6 text-sm space-y-1.5">
          <div className="flex items-center gap-2">
            <EnvelopeSimple weight="duotone" className="h-4 w-4 text-primary flex-none" />
            <a href={`mailto:${inquiry.customer_email}`} className="text-primary hover:underline truncate">
              {inquiry.customer_email}
            </a>
          </div>
          {inquiry.customer_phone && (
            <div className="flex items-center gap-2">
              <Phone weight="duotone" className="h-4 w-4 text-primary flex-none" />
              <a href={`tel:${inquiry.customer_phone}`} className="text-primary hover:underline">
                {inquiry.customer_phone}
              </a>
            </div>
          )}
        </div>

        {/* Pro gate / reply form */}
        {isLockedOut ? (
          <div
            className="rounded-2xl border border-border bg-muted/40 p-5 text-center"
            data-testid="owner-inquiry-locked-banner"
          >
            <XCircle weight="duotone" className="h-10 w-10 mx-auto text-muted-foreground" />
            <h3 className="mt-3 font-display text-lg font-semibold">This inquiry window has closed</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              {inquiry.customer_name.split(" ")[0]} has moved on. You&apos;re still in good standing and eligible for the next inquiry that matches your truck.
            </p>
            {recipient.locked_out_at && (
              <p className="mt-3 text-[11px] text-muted-foreground/80">
                Closed {new Date(recipient.locked_out_at).toLocaleString()}
              </p>
            )}
            <Link to="/dashboard" className="mt-4 inline-block">
              <Button variant="outline" size="sm">Back to dashboard</Button>
            </Link>
          </div>
        ) : !owner_is_pro ? (
          <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5 text-center">
            <Lock weight="duotone" className="h-10 w-10 mx-auto text-primary" />
            <h3 className="mt-3 font-display text-lg font-semibold">Replying through TruckTrackr is a Pro feature</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Pro lets you reply with templates, keeps your personal email private (your contact email is the Reply-To), and tracks responses on your dashboard.
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              You can still reply right now — just hit Reply on the email we sent you, or
              <a href={`mailto:${inquiry.customer_email}?subject=Re: Your TruckTrackr inquiry`} className="text-primary font-semibold ml-1">
                email {inquiry.customer_name.split(" ")[0]} directly
              </a>.
            </p>
            <Link to="/upgrade" className="mt-4 inline-block">
              <Button>See Pro plans →</Button>
            </Link>
          </div>
        ) : alreadyReplied ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-950/20 p-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle weight="fill" className="h-5 w-5 text-emerald-600" />
              <span className="font-semibold">
                {recipient.response_kind === "decline" ? "You declined this inquiry" : "You replied"}
              </span>
            </div>
            {recipient.response_summary && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-2">
                &ldquo;{recipient.response_summary}&rdquo;
                {recipient.response_summary.length === 200 && "…"}
              </p>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              Further messages go through your regular email — host hit Reply on our relay and your conversation is now direct.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reply-form">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground font-semibold mr-1">Quick start:</span>
              {QUICK_REPLIES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-card hover:border-primary/40 transition"
                  data-testid={`template-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={10}
              placeholder={`Hi ${inquiry.customer_name.split(" ")[0]},\n\nThanks for reaching out to ${truck?.truck_name || "us"}!…`}
              data-testid="reply-textarea"
            />
            <div className="text-[11px] text-muted-foreground">
              {reply.length}/3000 · Your reply will be emailed to {inquiry.customer_email} with Reply-To set to your account email.
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
              <Button
                variant="outline"
                onClick={() => setDeclineMode((v) => !v)}
                disabled={sending}
                data-testid="decline-toggle-btn"
              >
                {declineMode ? "Cancel decline" : "Politely decline"}
              </Button>
              {declineMode ? (
                <Button
                  variant="destructive"
                  onClick={() => handleSend("decline")}
                  disabled={sending || reply.trim().length < 10}
                  data-testid="send-decline-btn"
                >
                  {sending ? (
                    <><CircleNotch weight="bold" className="h-4 w-4 mr-1.5 animate-spin" />Sending…</>
                  ) : (
                    <><XCircle weight="bold" className="h-4 w-4 mr-1.5" />Send decline</>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => handleSend("reply")}
                  disabled={sending || reply.trim().length < 10}
                  data-testid="send-reply-btn"
                >
                  {sending ? (
                    <><CircleNotch weight="bold" className="h-4 w-4 mr-1.5 animate-spin" />Sending…</>
                  ) : (
                    <><PaperPlaneTilt weight="bold" className="h-4 w-4 mr-1.5" />Send reply</>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
        <Icon weight="duotone" className="h-3.5 w-3.5 text-primary" />
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}
